import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import { X, LogOut, AlertTriangle } from 'lucide-react';
import { useThemeContext } from '../context/ThemeContext';

export const LogoutConfirmDialog = ({ open, onClose, onConfirm }) => {
  const { mode } = useThemeContext();
  const isLight = mode === 'light';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '24px',
          padding: '12px',
          background: isLight 
            ? 'rgba(255, 255, 255, 0.9)' 
            : 'rgba(23, 23, 26, 0.95)',
          backdropFilter: 'blur(20px)',
          border: isLight 
            ? '1px solid rgba(0, 0, 0, 0.08)' 
            : '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: isLight
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.1)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          maxWidth: '400px',
          width: '100%'
        }
      }}
    >
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <IconButton onClick={onClose} size="small" sx={{ color: isLight ? '#94a3b8' : '#64748b' }}>
          <X size={18} />
        </IconButton>
      </Box>

      <DialogTitle sx={{ pb: 1, pt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            width: 40, 
            height: 40, 
            borderRadius: '12px',
            background: isLight ? '#fee2e2' : 'rgba(239, 68, 68, 0.15)',
            color: '#ef4444'
          }}>
            <AlertTriangle size={20} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: isLight ? '#0f172a' : '#f8fafc' }}>
            Confirm Logout
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        <Typography sx={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '0.95rem', lineHeight: 1.6 }}>
          Are you sure you want to sign out? You will need to log in again to access your interactive lessons and track your progress.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ gap: 1.5, px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          sx={{
            flex: 1,
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            py: 1.5,
            color: isLight ? '#64748b' : '#a1a1aa',
            '&:hover': { background: isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.05)' }
          }}
        >
          Stay Logged In
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          startIcon={<LogOut size={18} />}
          sx={{
            flex: 1,
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 800,
            py: 1.5,
            background: '#2563eb',
            boxShadow: '0 8px 16px -4px rgba(37, 99, 235, 0.3)',
            '&:hover': {
              background: '#1d4ed8',
              boxShadow: '0 12px 20px -4px rgba(37, 99, 235, 0.4)',
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          Logout
        </Button>
      </DialogActions>
    </Dialog>
  );
};
